import type { Event } from '@cocoonspace/sdk-js/types/event'
import { APP_CONFIG } from '@cocoonspace/shared/config'
import { type CalendarEvent, google, ics, outlook } from 'calendar-link'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import { CalendarLink } from './calendar-link'

export const useCalendarLinks = (event: Event) => {
	const { t } = useTranslation()

	return useMemo(() => {
		const formatedEvent: CalendarEvent = {
			title: event.space?.title || '',
			start: event.start_local,
			duration: [event.duration!, 'minutes'],
			location: `${event.space?.address?.address1}, ${event.space?.address?.postal_code} ${event.space?.address?.city}`,
			description: t('cwEvents:genericDescription', {
				url: `${APP_CONFIG.appUrl}/event/${event.id}`,
			}) as string,
			organizer: {
				name: `${event.organizer?.first_name} ${event.organizer?.last_name}`,
				email:
					APP_CONFIG.appEnv === 'preprod'
						? 'ical-pp@cocoon-space.com'
						: 'ical@cocoon-space.com',
			},
		}

		return [
			{
				'data-testid': 'google-link',
				text: 'Google',
				icon: '/images/v2/brands/gcal.png',
				value: google(formatedEvent),
			},
			{
				'data-testid': 'outlook-link',
				text: 'Outlook',
				icon: '/images/v2/brands/outlook.png',
				value: outlook(formatedEvent),
			},
			{
				'data-testid': 'ical-link',
				text: 'iCal',
				icon: '/images/v2/brands/apple.png',
				value: ics(formatedEvent),
			},
		]
	}, [
		t,
		event.duration,
		event.id,
		event.start_local,
		event.space?.title,
		event.space?.address?.address1,
		event.space?.address?.postal_code,
		event.space?.address?.city,
	])
}

export const AddToCalendar = ({
	event,
	children,
}: {
	event: Pick<Event, 'space' | 'start_local' | 'duration' | 'id'>
	children: JSX.Element
}) => {
	const calendarLinks = useCalendarLinks(event)

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>

			<DropdownMenuContent>
				{calendarLinks.map((link) => (
					<DropdownMenuItem key={link.text}>
						<CalendarLink
							link={link}
							size={22}
						/>
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
